import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Calendar, 
  Clock, 
  Trash2, 
  Plus, 
  Edit2,
  Save,
  X,
  ChevronDown,
  ChevronUp,
  Loader2,
  User,
  Mail,
  Phone,
  Globe,
  MessageSquare,
  FileText,
  CheckCircle,
  XCircle,
  AlertCircle,
  Settings,
  Bell
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BookingManagement = ({ getAuthHeader }) => {
  const [activeSubTab, setActiveSubTab] = useState('availability'); // availability, questions, bookings
  const [loading, setLoading] = useState(false);
  
  // Availability states
  const [dates, setDates] = useState([]);
  const [newDate, setNewDate] = useState('');
  const [expandedDate, setExpandedDate] = useState(null);
  const [newSlot, setNewSlot] = useState({ start: '', end: '' });
  
  // Questions states
  const [questions, setQuestions] = useState([]);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [newQuestion, setNewQuestion] = useState({
    question: '',
    question_type: 'text',
    options: [],
    required: true,
    order: 0
  });
  const [showNewQuestion, setShowNewQuestion] = useState(false);
  const [newOption, setNewOption] = useState('');
  
  // Bookings states
  const [bookings, setBookings] = useState([]);
  const [expandedBooking, setExpandedBooking] = useState(null);
  
  // Settings states
  const [settings, setSettings] = useState({
    reminder_hours: 7,
    admin_timezone: 'Asia/Dhaka',
    time_format: '12'
  });
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaved, setSettingsSaved] = useState(false);

  // Common timezone list
  const timezones = [
    'Asia/Dhaka',
    'Asia/Kolkata',
    'Asia/Dubai',
    'Asia/Singapore',
    'Asia/Tokyo',
    'Asia/Shanghai',
    'Europe/London',
    'Europe/Paris',
    'Europe/Berlin',
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'America/Toronto',
    'Australia/Sydney',
    'Pacific/Auckland',
    'UTC'
  ];

  useEffect(() => {
    if (activeSubTab === 'availability') fetchDates();
    if (activeSubTab === 'questions') fetchQuestions();
    if (activeSubTab === 'bookings') fetchBookings();
    if (activeSubTab === 'settings') fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSubTab]);

  // =============== AVAILABILITY FUNCTIONS ===============
  const fetchDates = async () => {
    const headers = getAuthHeader();
    if (!headers) return;
    
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/booking/availability`, { headers });
      const data = await res.json();
      setDates(data);
    } catch (error) {
      console.error('Error fetching dates:', error);
    }
    setLoading(false);
  };

  const createDate = async () => {
    if (!newDate) return;
    const headers = getAuthHeader();
    if (!headers) return;

    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/booking/dates`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: newDate, is_open: true })
      });
      
      if (res.ok) {
        setNewDate('');
        fetchDates();
      }
    } catch (error) {
      console.error('Error creating date:', error);
    }
  };

  const toggleDateOpen = async (dateId, currentStatus) => {
    const headers = getAuthHeader();
    if (!headers) return;

    try {
      await fetch(`${BACKEND_URL}/api/admin/booking/dates/${dateId}?is_open=${!currentStatus}`, {
        method: 'PUT',
        headers
      });
      fetchDates();
    } catch (error) {
      console.error('Error updating date:', error);
    }
  };

  const deleteDate = async (dateId) => {
    if (!window.confirm('Delete this date and all its slots?')) return;
    const headers = getAuthHeader();
    if (!headers) return;

    try {
      await fetch(`${BACKEND_URL}/api/admin/booking/dates/${dateId}`, {
        method: 'DELETE',
        headers
      });
      fetchDates();
    } catch (error) {
      console.error('Error deleting date:', error);
    }
  };

  const createSlot = async (dateId) => {
    if (!newSlot.start || !newSlot.end) return;
    const headers = getAuthHeader();
    if (!headers) return;

    try {
      await fetch(`${BACKEND_URL}/api/admin/booking/slots`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date_id: dateId,
          start_time: newSlot.start,
          end_time: newSlot.end
        })
      });
      setNewSlot({ start: '', end: '' });
      fetchDates();
    } catch (error) {
      console.error('Error creating slot:', error);
    }
  };

  const toggleSlotStatus = async (dateId, slotId, currentStatus) => {
    const headers = getAuthHeader();
    if (!headers) return;

    const newStatus = currentStatus === 'booked' ? 'available' : 'booked';
    
    try {
      await fetch(`${BACKEND_URL}/api/admin/booking/slots/${dateId}/${slotId}?status=${newStatus}`, {
        method: 'PUT',
        headers
      });
      fetchDates();
    } catch (error) {
      console.error('Error toggling slot status:', error);
    }
  };

  const deleteSlot = async (dateId, slotId) => {
    const headers = getAuthHeader();
    if (!headers) return;

    try {
      await fetch(`${BACKEND_URL}/api/admin/booking/slots/${dateId}/${slotId}`, {
        method: 'DELETE',
        headers
      });
      fetchDates();
    } catch (error) {
      console.error('Error deleting slot:', error);
    }
  };

  // =============== QUESTIONS FUNCTIONS ===============
  const fetchQuestions = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/booking/questions`);
      const data = await res.json();
      setQuestions(data);
    } catch (error) {
      console.error('Error fetching questions:', error);
    }
    setLoading(false);
  };

  const createQuestion = async () => {
    if (!newQuestion.question) return;
    const headers = getAuthHeader();
    if (!headers) return;

    try {
      await fetch(`${BACKEND_URL}/api/admin/booking/questions`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newQuestion,
          order: questions.length
        })
      });
      setNewQuestion({ question: '', question_type: 'text', options: [], required: true, order: 0 });
      setShowNewQuestion(false);
      fetchQuestions();
    } catch (error) {
      console.error('Error creating question:', error);
    }
  };

  const updateQuestion = async (questionId, data) => {
    const headers = getAuthHeader();
    if (!headers) return;

    try {
      await fetch(`${BACKEND_URL}/api/admin/booking/questions/${questionId}`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      setEditingQuestion(null);
      fetchQuestions();
    } catch (error) {
      console.error('Error updating question:', error);
    }
  };

  const deleteQuestion = async (questionId) => {
    if (!window.confirm('Delete this question?')) return;
    const headers = getAuthHeader();
    if (!headers) return;

    try {
      await fetch(`${BACKEND_URL}/api/admin/booking/questions/${questionId}`, {
        method: 'DELETE',
        headers
      });
      fetchQuestions();
    } catch (error) {
      console.error('Error deleting question:', error);
    }
  };

  // =============== BOOKINGS FUNCTIONS ===============
  const fetchBookings = async () => {
    const headers = getAuthHeader();
    if (!headers) return;

    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/bookings`, { headers });
      const data = await res.json();
      setBookings(data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    }
    setLoading(false);
  };

  const updateBookingStatus = async (bookingId, status) => {
    const headers = getAuthHeader();
    if (!headers) return;

    try {
      await fetch(`${BACKEND_URL}/api/admin/bookings/${bookingId}/status?status=${status}`, {
        method: 'PUT',
        headers
      });
      fetchBookings();
    } catch (error) {
      console.error('Error updating booking:', error);
    }
  };

  const deleteBooking = async (bookingId) => {
    if (!window.confirm('Delete this booking?')) return;
    const headers = getAuthHeader();
    if (!headers) return;

    try {
      await fetch(`${BACKEND_URL}/api/admin/bookings/${bookingId}`, {
        method: 'DELETE',
        headers
      });
      fetchBookings();
    } catch (error) {
      console.error('Error deleting booking:', error);
    }
  };

  // =============== SETTINGS FUNCTIONS ===============
  const fetchSettings = async () => {
    const headers = getAuthHeader();
    if (!headers) return;

    setSettingsLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/settings`, { headers });
      const data = await res.json();
      setSettings({
        reminder_hours: data.reminder_hours || 7,
        admin_timezone: data.admin_timezone || 'Asia/Dhaka',
        time_format: data.time_format || '12'
      });
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
    setSettingsLoading(false);
  };

  const saveSettings = async () => {
    const headers = getAuthHeader();
    if (!headers) return;

    setSettingsLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/settings`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });
      
      if (res.ok) {
        setSettingsSaved(true);
        setTimeout(() => setSettingsSaved(false), 3000);
      }
    } catch (error) {
      console.error('Error saving settings:', error);
    }
    setSettingsLoading(false);
  };

  return (
    <div>
      {/* Sub-tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {[
          { id: 'availability', label: 'Availability', icon: Calendar },
          { id: 'questions', label: 'Questions', icon: FileText },
          { id: 'bookings', label: 'Bookings', icon: User },
          { id: 'settings', label: 'Settings', icon: Settings }
        ].map(tab => (
          <Button
            key={tab.id}
            variant={activeSubTab === tab.id ? 'default' : 'ghost'}
            onClick={() => setActiveSubTab(tab.id)}
            className="flex items-center gap-2 whitespace-nowrap"
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </Button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : (
        <>
          {/* AVAILABILITY TAB */}
          {activeSubTab === 'availability' && (
            <div className="space-y-4">
              {/* Add new date */}
              <div className="glass-strong rounded-xl p-4 flex flex-wrap gap-3 items-center">
                <Input
                  type="date"
                  value={newDate}
                  onChange={(e) => setNewDate(e.target.value)}
                  className="w-auto"
                />
                <Button onClick={createDate} className="flex items-center gap-2">
                  <Plus className="w-4 h-4" />
                  Add Date
                </Button>
              </div>

              {/* Dates list */}
              {dates.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No dates available. Add a date to get started.</p>
              ) : (
                dates.map(date => (
                  <motion.div
                    key={date.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="glass-strong rounded-xl overflow-hidden"
                  >
                    <div 
                      className="p-4 flex items-center justify-between cursor-pointer hover:bg-muted/50"
                      onClick={() => setExpandedDate(expandedDate === date.id ? null : date.id)}
                    >
                      <div className="flex items-center gap-4">
                        <Calendar className="w-5 h-5 text-primary" />
                        <div>
                          <p className="font-semibold text-foreground">
                            {new Date(date.date).toLocaleDateString('en-US', { 
                              weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' 
                            })}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {date.slots?.length || 0} slots • {date.is_open ? 'Open' : 'Closed'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); toggleDateOpen(date.id, date.is_open); }}
                          className={date.is_open ? 'text-green-500' : 'text-red-500'}
                        >
                          {date.is_open ? 'Open' : 'Closed'}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); deleteDate(date.id); }}
                          className="text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                        {expandedDate === date.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </div>
                    </div>

                    <AnimatePresence>
                      {expandedDate === date.id && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="border-t border-border"
                        >
                          <div className="p-4 space-y-4">
                            {/* Add slot */}
                            <div className="flex flex-wrap gap-2 items-center">
                              <Input
                                type="time"
                                value={newSlot.start}
                                onChange={(e) => setNewSlot(prev => ({ ...prev, start: e.target.value }))}
                                className="w-auto"
                                placeholder="Start"
                              />
                              <span className="text-muted-foreground">to</span>
                              <Input
                                type="time"
                                value={newSlot.end}
                                onChange={(e) => setNewSlot(prev => ({ ...prev, end: e.target.value }))}
                                className="w-auto"
                                placeholder="End"
                              />
                              <Button size="sm" onClick={() => createSlot(date.id)}>
                                <Plus className="w-4 h-4 mr-1" />
                                Add Slot
                              </Button>
                            </div>

                            {/* Slots list */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                              {date.slots?.map(slot => (
                                <div 
                                  key={slot.id} 
                                  className={`p-3 rounded-lg border ${
                                    slot.status === 'available' ? 'border-green-500/30 bg-green-500/10' :
                                    slot.status === 'booked' ? 'border-red-500/30 bg-red-500/10' :
                                    'border-yellow-500/30 bg-yellow-500/10'
                                  }`}
                                >
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                      <Clock className="w-4 h-4 text-muted-foreground" />
                                      <span className="text-sm font-semibold">{slot.start_time} - {slot.end_time}</span>
                                    </div>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => deleteSlot(date.id, slot.id)}
                                      className="h-6 w-6 p-0 text-red-500 hover:text-red-600"
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  </div>
                                  <div className="flex items-center justify-between">
                                    <span className={`text-xs font-bold uppercase ${
                                      slot.status === 'available' ? 'text-green-500' :
                                      slot.status === 'booked' ? 'text-red-500' : 'text-yellow-500'
                                    }`}>
                                      {slot.status}
                                    </span>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => toggleSlotStatus(date.id, slot.id, slot.status)}
                                      className={`text-xs h-7 ${
                                        slot.status === 'booked' 
                                          ? 'border-green-500 text-green-500 hover:bg-green-500/10' 
                                          : 'border-red-500 text-red-500 hover:bg-red-500/10'
                                      }`}
                                    >
                                      {slot.status === 'booked' ? 'Mark Available' : 'Mark Booked'}
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                ))
              )}
            </div>
          )}

          {/* QUESTIONS TAB */}
          {activeSubTab === 'questions' && (
            <div className="space-y-4">
              <Button 
                onClick={() => setShowNewQuestion(!showNewQuestion)}
                className="flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Question
              </Button>

              {/* New question form */}
              <AnimatePresence>
                {showNewQuestion && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="glass-strong rounded-xl p-4 space-y-4"
                  >
                    <Input
                      value={newQuestion.question}
                      onChange={(e) => setNewQuestion(prev => ({ ...prev, question: e.target.value }))}
                      placeholder="Question text..."
                    />
                    <div className="flex flex-wrap gap-2">
                      {['text', 'textarea', 'select', 'radio'].map(type => (
                        <Button
                          key={type}
                          variant={newQuestion.question_type === type ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setNewQuestion(prev => ({ ...prev, question_type: type }))}
                        >
                          {type}
                        </Button>
                      ))}
                    </div>
                    
                    {(newQuestion.question_type === 'select' || newQuestion.question_type === 'radio') && (
                      <div className="space-y-2">
                        <p className="text-sm text-muted-foreground">Options:</p>
                        <div className="flex flex-wrap gap-2">
                          {newQuestion.options.map((opt, idx) => (
                            <span key={idx} className="px-3 py-1 bg-muted rounded-full text-sm flex items-center gap-2">
                              {opt}
                              <button onClick={() => setNewQuestion(prev => ({
                                ...prev,
                                options: prev.options.filter((_, i) => i !== idx)
                              }))}>
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                        <div className="flex gap-2">
                          <Input
                            value={newOption}
                            onChange={(e) => setNewOption(e.target.value)}
                            placeholder="Add option..."
                            className="flex-1"
                          />
                          <Button size="sm" onClick={() => {
                            if (newOption) {
                              setNewQuestion(prev => ({ ...prev, options: [...prev.options, newOption] }));
                              setNewOption('');
                            }
                          }}>
                            Add
                          </Button>
                        </div>
                      </div>
                    )}
                    
                    <div className="flex gap-2">
                      <Button onClick={createQuestion}>Save Question</Button>
                      <Button variant="ghost" onClick={() => setShowNewQuestion(false)}>Cancel</Button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Questions list */}
              {questions.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No questions yet. Add questions for the booking funnel.</p>
              ) : (
                questions.map((q, idx) => (
                  <motion.div
                    key={q.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="glass-strong rounded-xl p-4"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <span className="text-xs text-primary font-medium">Q{idx + 1} • {q.question_type}</span>
                        <p className="font-medium text-foreground mt-1">{q.question}</p>
                        {q.options?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {q.options.map((opt, i) => (
                              <span key={i} className="px-2 py-0.5 bg-muted rounded text-xs">{opt}</span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm" onClick={() => deleteQuestion(q.id)} className="text-red-500">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
            </div>
          )}

          {/* BOOKINGS TAB */}
          {activeSubTab === 'bookings' && (
            <div className="space-y-4">
              {bookings.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No bookings yet.</p>
              ) : (
                bookings.map(booking => (
                  <motion.div
                    key={booking.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="glass-strong rounded-xl overflow-hidden"
                  >
                    <div 
                      className="p-4 flex items-center justify-between cursor-pointer hover:bg-muted/50"
                      onClick={() => setExpandedBooking(expandedBooking === booking.id ? null : booking.id)}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          booking.status === 'confirmed' ? 'bg-green-500/20 text-green-500' :
                          booking.status === 'cancelled' ? 'bg-red-500/20 text-red-500' :
                          'bg-blue-500/20 text-blue-500'
                        }`}>
                          {booking.status === 'confirmed' ? <CheckCircle className="w-5 h-5" /> :
                           booking.status === 'cancelled' ? <XCircle className="w-5 h-5" /> :
                           <AlertCircle className="w-5 h-5" />}
                        </div>
                        <div>
                          <p className="font-semibold text-foreground">{booking.user_name}</p>
                          <p className="text-sm text-muted-foreground">
                            {new Date(booking.date).toLocaleDateString()} at {booking.start_time}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          booking.status === 'confirmed' ? 'bg-green-500/20 text-green-500' :
                          booking.status === 'cancelled' ? 'bg-red-500/20 text-red-500' :
                          'bg-blue-500/20 text-blue-500'
                        }`}>
                          {booking.status}
                        </span>
                        {expandedBooking === booking.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </div>
                    </div>

                    <AnimatePresence>
                      {expandedBooking === booking.id && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="border-t border-border p-4 space-y-4"
                        >
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="flex items-center gap-2">
                              <Mail className="w-4 h-4 text-muted-foreground" />
                              <span className="text-sm">{booking.user_email}</span>
                            </div>
                            {booking.user_phone && (
                              <div className="flex items-center gap-2">
                                <Phone className="w-4 h-4 text-muted-foreground" />
                                <span className="text-sm">{booking.user_phone}</span>
                              </div>
                            )}
                            <div className="flex items-center gap-2">
                              <Globe className="w-4 h-4 text-muted-foreground" />
                              <span className="text-sm">{booking.user_timezone}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4 text-muted-foreground" />
                              <span className="text-sm">{booking.start_time} - {booking.end_time}</span>
                            </div>
                          </div>

                          {Object.keys(booking.questionnaire_answers || {}).length > 0 && (
                            <div className="space-y-2">
                              <p className="text-sm font-medium text-foreground">Questionnaire Answers:</p>
                              <div className="bg-muted/50 rounded-lg p-3 space-y-2">
                                {Object.entries(booking.questionnaire_answers).map(([key, value]) => (
                                  <div key={key} className="text-sm">
                                    <span className="text-muted-foreground">Q: </span>
                                    <span className="text-foreground">{value}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          <div className="flex gap-2 pt-2">
                            {booking.status === 'confirmed' && (
                              <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => updateBookingStatus(booking.id, 'cancelled')}
                                className="text-red-500"
                              >
                                Cancel Booking
                              </Button>
                            )}
                            {booking.status === 'cancelled' && (
                              <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => updateBookingStatus(booking.id, 'confirmed')}
                                className="text-green-500"
                              >
                                Restore Booking
                              </Button>
                            )}
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => deleteBooking(booking.id)}
                              className="text-red-500"
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Delete
                            </Button>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                ))
              )}
            </div>
          )}

          {/* SETTINGS TAB */}
          {activeSubTab === 'settings' && (
            <div className="space-y-6">
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass-strong rounded-xl p-6 space-y-6"
              >
                <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                  <Settings className="w-5 h-5 text-primary" />
                  Booking Settings
                </h3>

                {/* Reminder Hours */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground flex items-center gap-2">
                    <Bell className="w-4 h-4 text-muted-foreground" />
                    Reminder Email Time
                  </label>
                  <p className="text-xs text-muted-foreground">
                    How many hours before the meeting should the reminder email be sent?
                  </p>
                  <div className="flex items-center gap-3">
                    <Input
                      type="number"
                      min="0.5"
                      max="72"
                      step="0.5"
                      value={settings.reminder_hours}
                      onChange={(e) => setSettings(prev => ({ 
                        ...prev, 
                        reminder_hours: parseFloat(e.target.value) || 7 
                      }))}
                      className="w-32"
                    />
                    <span className="text-sm text-muted-foreground">hours before meeting</span>
                  </div>
                </div>

                {/* Admin Timezone */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground flex items-center gap-2">
                    <Globe className="w-4 h-4 text-muted-foreground" />
                    Your Timezone
                  </label>
                  <p className="text-xs text-muted-foreground">
                    All time slots you create will be in this timezone. Visitors will see converted times.
                  </p>
                  <select
                    value={settings.admin_timezone}
                    onChange={(e) => setSettings(prev => ({ 
                      ...prev, 
                      admin_timezone: e.target.value 
                    }))}
                    className="w-full max-w-xs px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    {timezones.map(tz => (
                      <option key={tz} value={tz}>{tz}</option>
                    ))}
                  </select>
                </div>

                {/* Time Format */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground flex items-center gap-2">
                    <Clock className="w-4 h-4 text-muted-foreground" />
                    Time Format
                  </label>
                  <p className="text-xs text-muted-foreground">
                    Choose how times are displayed to visitors.
                  </p>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="time_format"
                        value="12"
                        checked={settings.time_format === '12'}
                        onChange={(e) => setSettings(prev => ({ 
                          ...prev, 
                          time_format: e.target.value 
                        }))}
                        className="w-4 h-4 text-primary"
                      />
                      <span className="text-sm text-foreground">12-hour (3:00 PM)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="time_format"
                        value="24"
                        checked={settings.time_format === '24'}
                        onChange={(e) => setSettings(prev => ({ 
                          ...prev, 
                          time_format: e.target.value 
                        }))}
                        className="w-4 h-4 text-primary"
                      />
                      <span className="text-sm text-foreground">24-hour (15:00)</span>
                    </label>
                  </div>
                </div>

                {/* Save Button */}
                <div className="flex items-center gap-4 pt-4 border-t border-border">
                  <Button 
                    onClick={saveSettings} 
                    disabled={settingsLoading}
                    className="flex items-center gap-2"
                  >
                    {settingsLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    Save Settings
                  </Button>
                  {settingsSaved && (
                    <motion.span
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0 }}
                      className="text-sm text-green-500 flex items-center gap-1"
                    >
                      <CheckCircle className="w-4 h-4" />
                      Settings saved!
                    </motion.span>
                  )}
                </div>
              </motion.div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BookingManagement;
